import { ModelType } from "~/enums/modelType";
import { ReferenceNodeType } from "~/enums/referenceNodeType";
import { GenericType } from '~/interfaces/genericTypeInterfaces';
import { IImposterInstructor } from '~/interfaces/imposterInterfaces';
import { IHasParent, IRepoModel, ITrackedModel } from '~/interfaces/modelInterfaces';
import {
  IAccountRelationMetadata,
  IObjectModel,
  IObjectModelProperty,
  IObjectModelRecordMetadata,
  IObjectModelRelation
} from '~/interfaces/objectModelInterfaces';
import { IProcessFlowVariable } from '~/interfaces/processFlowInterfaces';
import { IDateTimeDetails, INumberDetails, IPickListDetails, IBooleanDetails } from '~/interfaces/propertyInterfaces';
import { IUniversalValue } from '~/interfaces/universalValueInterfaces';
import { IWorkflowStepAction } from "~/interfaces/workflowStepInterface";
import DynamicInput from '~/models/dynamicInput';
import ExternalConnection from '~/models/externalConnections/externalConnection';
import ElementHandler from '~/services/elementHandler';
import { IForm } from '../../formability/form_builder/models/form';
import { IFormElement } from '../../formability/form_builder/models/formElements/formElement';
import { IWorkflowStep } from '../../formability/form_builder/models/workflow/workflowStep';
import EndPoint from "../../formability/organization_settings/models/connections/endPoint";
import {PermitteeType} from "~/enums/permitteeType";
import CertificateDynamicInput from "../../formability/certificateBuilder/models/certificateDynamicInput";
import Certificate, {TCertificate} from "../../formability/certificateBuilder/models/certificate";
import ExternalConnectionEnvironmentAPIHeaderPair from "~/models/externalConnections/externalConnectionEnvironmentAPIHeaderPair";
import FormSection from '@/formability/form_builder/models/formSections/formSection';

export interface IReferencePath extends ITrackedModel, IRepoModel {
  nodes: IReferencePathNodeGeneral[]
  nodesHandler: ElementHandler<IReferencePathNodeSpecific>
  lastNode: IReferencePathNodeSpecific;
  lastNodeService: IReferencePathServiceType
  referenceExists: boolean
  allReferencesExist: boolean
  allReferencesLinked: boolean
  /**
   * Is the reference at the end of this path considered a collection given the entire path context - taking into account all nodes, including Process Flow Variable
   * this is different from ReferencePathNode property with same name which only considers it's own self node
   */
  referenceIsCollection: boolean
  referenceIsSingle: boolean
  dynamicFieldName: string;

  initializeEmbedded(parent: IReferencePath & ITrackedModel, item: IReferencePathNodeSpecific & IHasParent<IReferencePath>, position?: number, json?: object, selectItem?: boolean)
  initializeEmbeddedNodes(nodes: IReferencePathNodeGeneral[]): void

  // backend details about last node's reference - not always present
  last_referenced_object?: Record<string, any>;
  nicePathString: string
}

export interface IReferencePathSpecific extends IReferencePath {
  nodes: IReferencePathNodeSpecific[]
}

export interface IOrphanReferencePathNode extends ITrackedModel, IRepoModel {
  label: string;
  reference_type: ReferenceNodeType;
  reference_id: string;
  reference_scope_type?: ReferenceNodeType
  reference_scope_id?: string
  inverse?: boolean;
  canLinkTo(node: IOrphanReferencePathNode): boolean
  isSingleUploader: boolean
  clearReferenceId(): void
}

export interface IParentedReferencePathNode extends ITrackedModel, IRepoModel {
  parent: IReferencePath;
}

export type ServiceReferenceType =
  IObjectModelRelation
  | IObjectModel
  | IProcessFlowVariable
  | IObjectModelProperty
  | IForm
  | IWorkflowStep
  | FormSection
  | IWorkflowStepAction
  | IFormElement
  | ExternalConnection
  | EndPoint
  | ReferenceNodeType // case for ApiMessageType
  | DynamicInput
  | Certificate
  | CertificateDynamicInput
  | ExternalConnectionEnvironmentAPIHeaderPair
  | IObjectModelRecordMetadata
  | IAccountRelationMetadata

interface IReferencePathNodeBase extends IOrphanReferencePathNode, IParentedReferencePathNode {}

export interface IReferencePathNodeObjectModelRelation extends IReferencePathNodeBase {
  reference(): IObjectModelRelation

  reference_type: ReferenceNodeType.ObjectModelRelation,
  service: IReferencePathServiceType<IObjectModelRelation>
}

export interface IReferencePathNodeObjectModelProperty extends IReferencePathNodeBase {
  reference(): IObjectModelProperty

  reference_type: ReferenceNodeType.ObjectModelProperty,
  service: IReferencePathServiceType<IObjectModelProperty>
}

export interface IReferencePathNodeObjectModelRecordMetadata extends IReferencePathNodeBase {
  reference(): IObjectModelRecordMetadata

  reference_type: ReferenceNodeType.ObjectModelRecordMetadata,
  service: IReferencePathServiceType<IObjectModelRecordMetadata>
}

export interface IReferencePathNodeObjectModelLinkedForm extends IReferencePathNodeBase {
  reference(): IForm

  reference_type: ReferenceNodeType.ObjectModelLinkedForm;
  service: IReferencePathServiceType<IForm>
}

export interface IReferencePathNodeAccountRelationMetadata extends IReferencePathNodeBase {
  reference(): IAccountRelationMetadata

  reference_type: ReferenceNodeType.AccountRelationMetadata,
  service: IReferencePathServiceType<IAccountRelationMetadata>
}

export interface IReferencePathNodeProcessFlowMetadata extends IReferencePathNodeBase {
  reference(): ReferenceNodeType;

  reference_type: ReferenceNodeType.ProcessFlowInitiatorFirstName | ReferenceNodeType.ProcessFlowInitiatorLastName | ReferenceNodeType.ProcessFlowInitiatorEmail | ReferenceNodeType.ProcessFlowTriggerTime
  service: IReferencePathServiceType<ReferenceNodeType>
}

export interface IReferencePathNodeWorkflowGeneratedCert extends IReferencePathNodeBase {
  reference(): IWorkflowStep;

  reference_type: ReferenceNodeType.WorkflowGeneratedCert;
  service: IReferencePathServiceType<IWorkflowStep>
}

export interface IReferencePathNodeObjectModel extends IReferencePathNodeBase {
  reference(): IObjectModel

  reference_type: ReferenceNodeType.ObjectModel,
  service: IReferencePathServiceType<IObjectModel>
}

export interface IReferencePathNodeApiConnection extends IReferencePathNodeBase {
  reference(): ExternalConnection

  reference_type: ReferenceNodeType.ApiConnection,
  service: IReferencePathServiceType<ExternalConnection>
}

export interface IReferencePathNodeApiEndPoint extends IReferencePathNodeBase {
  reference(): EndPoint

  reference_type: ReferenceNodeType.ApiEndpoint,
  service: IReferencePathServiceType<EndPoint>
}

export interface IReferencePathNodeApiMessageType extends IReferencePathNodeBase {
  reference(): ReferenceNodeType

  reference_type: ReferenceNodeType.ApiMessageType,
  service: IReferencePathServiceType<ReferenceNodeType>
}

export interface IReferencePathNodeApiDynamicInput extends IReferencePathNodeBase {
  reference(): DynamicInput

  reference_type: ReferenceNodeType.DynamicInput,
  service: IReferencePathServiceType<DynamicInput>
}

export interface IReferencePathNodeCertificate extends IReferencePathNodeBase {
  reference(): Certificate;

  reference_type: ReferenceNodeType.Certificate;
  service: IReferencePathServiceType<Certificate>;
}


export interface IReferencePathNodeCertificateDynamicInput extends IReferencePathNodeBase {
  reference(): CertificateDynamicInput;

  reference_type: ReferenceNodeType.CertificateDynamicInput;
  service: IReferencePathServiceType<CertificateDynamicInput>;
}

export interface IReferencePathNodeApiHeader extends IReferencePathNodeBase {
  reference(): ExternalConnectionEnvironmentAPIHeaderPair

  reference_type: ReferenceNodeType.ApiHeader,
  service: IReferencePathServiceType<ReferenceNodeType>
}

export interface IReferencePathNodeApiURLParameter extends IReferencePathNodeBase {
  reference(): any;

  reference_type: ReferenceNodeType.ApiURLParameter,
  service: IReferencePathServiceType<ReferenceNodeType>
}

export interface IReferencePathNodeProcessFlowVariable extends IReferencePathNodeBase {
  reference(): IProcessFlowVariable

  reference_type: ReferenceNodeType.ProcessFlowVariable,
  service: IReferencePathServiceType<IProcessFlowVariable>
}

export interface IReferencePathNodeForm extends IReferencePathNodeBase {
  reference(): IForm

  reference_type: ReferenceNodeType.Form,
  service: IReferencePathServiceType<IForm>
}

export interface IReferencePathNodeWorkflowStep extends IReferencePathNodeBase {
  reference(): IWorkflowStep

  reference_type: ReferenceNodeType.WorkflowStep,
  service: IReferencePathServiceType<IWorkflowStep>
}

export interface IReferencePathNodeFormSection extends IReferencePathNodeBase {
  reference(): FormSection

  reference_type: ReferenceNodeType.FormSection,
  service: IReferencePathServiceType<FormSection>
}

export interface IReferencePathNodeWorkflowStepAction extends IReferencePathNodeBase {
  reference(): IWorkflowStepAction

  reference_type: ReferenceNodeType.WorkflowStepAction,
  service: IReferencePathServiceType<IWorkflowStepAction>
}

export interface IReferencePathNodeFormElement extends IReferencePathNodeBase {
  reference(): IFormElement

  reference_type: ReferenceNodeType.FormElement,
  service: IReferencePathServiceType<IFormElement>
}

export interface IReferencePathNodeOriginalSubmitter extends IReferencePathNodeBase {
  reference(): ReferenceNodeType

  reference_type: ReferenceNodeType.OriginalSubmitter
  service: IReferencePathServiceType<ReferenceNodeType>
}

export interface IReferencePathNodeAllCompleters extends IReferencePathNodeBase {
  reference(): ReferenceNodeType;

  reference_type: ReferenceNodeType.AllCompleters;
  service: IReferencePathServiceType<ReferenceNodeType>;
}

export interface IReferencePathNodeStepCompleter extends IReferencePathNodeBase {
  reference(): ReferenceNodeType;

  reference_type: ReferenceNodeType.StepCompleter;
  service: IReferencePathServiceType<ReferenceNodeType>;
}

export interface IReferencePathNodeRecordHolder extends IReferencePathNodeBase {
  reference(): ReferenceNodeType

  reference_type: ReferenceNodeType.RecordHolder
  service: IReferencePathServiceType<ReferenceNodeType>
}

export interface IReferencePathNodeFormSubmissionDate extends IReferencePathNodeBase {
  reference(): ReferenceNodeType

  reference_type: ReferenceNodeType.FormSubmissionDate
  service: IReferencePathServiceType<ReferenceNodeType>
}

export interface IReferencePathNodeFormSubmissionID extends IReferencePathNodeBase {
  reference(): ReferenceNodeType

  reference_type: ReferenceNodeType.FormSubmissionID
  service: IReferencePathServiceType<ReferenceNodeType>
}

export interface IReferencePathNodeWorkflowStepStart extends IReferencePathNodeBase {
  reference(): IWorkflowStep

  reference_type: ReferenceNodeType.WorkflowStepStart
  service: IReferencePathServiceType<ReferenceNodeType>
}

export interface IReferencePathNodeWorkflowStepEnd extends IReferencePathNodeBase {
  reference(): IWorkflowStep

  reference_type: ReferenceNodeType.WorkflowStepEnd
  service: IReferencePathServiceType<ReferenceNodeType>
}

export interface IReferencePathNodeFormPaymentsTotalCost extends IReferencePathNodeBase {
  reference(): ReferenceNodeType

  reference_type: ReferenceNodeType.FormPaymentsTotalCost
  service: IReferencePathServiceType<ReferenceNodeType>
}

export interface IReferencePathNodeFormPaymentsType extends IReferencePathNodeBase {
  reference(): ReferenceNodeType

  reference_type: ReferenceNodeType.FormPaymentsType
  service: IReferencePathServiceType<ReferenceNodeType>
}

export interface IReferencePathNodeFormPaymentsDate extends IReferencePathNodeBase {
  reference(): ReferenceNodeType

  reference_type: ReferenceNodeType.FormPaymentsDate
  service: IReferencePathServiceType<ReferenceNodeType>
}

export interface IReferencePathNodeFormPaymentsLineItem extends IReferencePathNodeBase {
  reference(): ReferenceNodeType

  reference_type: ReferenceNodeType.FormPaymentsLineItem
  service: IReferencePathServiceType<ReferenceNodeType>
}

export type IReferencePathNodeFormSubmissionMetadata = IReferencePathNodeFormSubmissionID;

export type IReferencePathNodePayments = IReferencePathNodeFormPaymentsTotalCost | IReferencePathNodeFormPaymentsType | IReferencePathNodeFormPaymentsDate | IReferencePathNodeFormPaymentsLineItem

export type IReferenceWorkflowStepDate = IReferencePathNodeWorkflowStepStart | IReferencePathNodeWorkflowStepEnd

export type IReferencePathNodeUserData = IReferencePathNodeOriginalSubmitter | IReferencePathNodeRecordHolder | IReferencePathNodeAllCompleters | IReferencePathNodeStepCompleter

export type IReferencePathNodeSpecific =
  IReferencePathNodeObjectModelRelation |
  IReferencePathNodeObjectModelProperty |
  IReferencePathNodeObjectModelRecordMetadata |
  IReferencePathNodeObjectModelLinkedForm |
  IReferencePathNodeObjectModel |
  IReferencePathNodeProcessFlowVariable |
  IReferencePathNodeForm |
  IReferencePathNodeWorkflowStep |
  IReferencePathNodeFormSection |
  IReferencePathNodeWorkflowStepAction |
  IReferencePathNodeFormElement |
  IReferencePathNodeOriginalSubmitter |
  IReferencePathNodeAllCompleters |
  IReferencePathNodeStepCompleter |
  IReferencePathNodeFormSubmissionDate |
  IReferencePathNodeFormSubmissionID |
  IReferencePathNodeFormPaymentsTotalCost |
  IReferencePathNodeFormPaymentsType |
  IReferencePathNodeFormPaymentsDate |
  IReferencePathNodeFormPaymentsLineItem |
  IReferencePathNodeWorkflowStepStart |
  IReferencePathNodeWorkflowStepEnd |
  IReferencePathNodeApiConnection |
  IReferencePathNodeApiEndPoint |
  IReferencePathNodeApiMessageType |
  IReferencePathNodeApiDynamicInput |
  IReferencePathNodeRecordHolder |
  IReferencePathNodeAccountRelationMetadata |
  IReferencePathNodeProcessFlowMetadata |
  IReferencePathNodeWorkflowGeneratedCert |
  IReferencePathNodeApiHeader |
  IReferencePathNodeApiURLParameter |
  IReferencePathNodeCertificate |
  IReferencePathNodeCertificateDynamicInput

export type IReferencePathNodeGeneral<T = any> = IReferencePathNodeBase & {
  reference(): T
  service: any
}

export interface IReferencePathServiceType<R = ServiceReferenceType> extends IImposterInstructor, IUniversalValue {
  label: string;
  nodes: IReferencePathNodeSpecific[]
  referenceExists: boolean
  /**
   * Is the reference for the given nodes considered a collection on its own - without taking into account nodes "above"
   */
  referenceIsCollection: boolean
  referenceIsSingle: boolean
  referenceIsAppendable: boolean
  referenceIsInitializable: boolean
  referenceIsSelectable: boolean
  referenceIsRemovable: boolean
  referenceIsNotClearable: boolean

  referenceIsPickList(): this is { referenceDetails: IReferencePathReferenceDetailsPickList }

  referenceIsDateTime(): this is { referenceDetails: IReferencePathReferenceDetailsDateTimePicker }
  /**
   * ModelType of the underlying main node of focus
   */
  referenceModelType: ModelType;
  referenceGenericModelType: GenericType;
  referencePermitteeType?: PermitteeType;
  referenceDetails: IReferencePathReferenceDetails
  breadcrumbs: string[]

  reference(): R

  buildReference(attrs: Record<string, any>): R

  findOrBuildReference(attrs: Record<string, any>): R

  canLinkTo(node: IReferencePathNodeSpecific): boolean
  toDisplayValue(value: any, ...args: any[]): any
}

/**
 * Something that can be the reference of a reference node (e.g. a Form, Object Model, see ReferenceNodeType)
 */
export interface IReferenceNodeable {
  referenceNodeType: ReferenceNodeType
}

export enum ReferencePathReferenceDetailsType {
  Text,
  DateTimePicker,
  PickList,
  Number,
  User,
  Boolean,
}

export interface IReferencePathReferenceDetailsText {
  type: ReferencePathReferenceDetailsType.Text
}

export interface IReferencePathReferenceDetailsUser {
  type: ReferencePathReferenceDetailsType.User
}

export type IReferencePathReferenceDetailsDateTimePicker =
  { type: ReferencePathReferenceDetailsType.DateTimePicker }
  & Partial<IDateTimeDetails>

export type IReferencePathReferenceDetailsPickList =
  { type: ReferencePathReferenceDetailsType.PickList }
  & Partial<IPickListDetails>

export type IReferencePathReferenceDetailsNumber =
  { type: ReferencePathReferenceDetailsType.Number }
  & Partial<INumberDetails>

export type IReferencePathReferenceDetailsBoolean =
  { type: ReferencePathReferenceDetailsType.Boolean }
  & Partial<IBooleanDetails>

export type IReferencePathReferenceDetails = IReferencePathReferenceDetailsText
  | IReferencePathReferenceDetailsDateTimePicker
  | IReferencePathReferenceDetailsPickList
  | IReferencePathReferenceDetailsNumber
  | IReferencePathReferenceDetailsUser
  | IReferencePathReferenceDetailsBoolean
